import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../auth';

interface ApiToken {
  token: string;
  created_at: string | null;
  last_used: string | null;
}

export function APIDocs() {
  const { user } = useAuth();
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const loadTokens = async () => {
    setLoadingTokens(true);
    setError('');
    
    try {
      const response = await fetch('/api/tokens', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setTokens(data.api_tokens || []);
        } else {
          const responseText = await response.text();
          console.error('Response is not JSON:', responseText);
          setError('Ошибка загрузки токенов: неверный формат ответа');
        }
      } else {
        console.error('Failed to load tokens:', response.status, response.statusText);
        setError(`Ошибка загрузки токенов: ${response.status}`);
      }
    } catch (err) {
      console.error('Error loading tokens:', err);
      setError('Ошибка сети при загрузке токенов');
    } finally {
      setLoadingTokens(false);
    }
  };

  useEffect(() => {
    loadTokens();
  }, []);

  const generateToken = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch('/api/generate_token', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (data.ok) {
            setSuccess(`Новый токен создан: ${data.token}`);
            await loadTokens();
          } else {
            setError(data.error || 'Ошибка создания токена');
          }
        } else {
          setError('Сервер вернул неверный формат ответа');
        }
      } else {
        setError(`Ошибка HTTP: ${response.status}`);
      }
    } catch (err) {
      console.error('Generate token error:', err);
      setError('Ошибка при создании токена');
    } finally {
      setLoading(false);
    }
  };

  const deleteToken = async (token: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот токен?')) {
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`/api/delete_token/${token}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (data.ok) {
            setSuccess('Токен успешно удален');
            await loadTokens();
          } else {
            setError(data.error || 'Ошибка удаления токена');
          }
        } else {
          setError('Сервер вернул неверный формат ответа');
        }
      } else {
        setError(`Ошибка HTTP: ${response.status}`);
      }
    } catch (err) {
      console.error('Delete token error:', err);
      setError('Ошибка при удалении токена');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Токен скопирован в буфер обмена');
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">API Документация</h1>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Управление токенами */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">API Токены</h2>
        <p className="text-muted-foreground mb-4">
          Создайте API токен для доступа к поисковому API. Токен необходим для всех запросов.
        </p>

        <div className="flex gap-4 mb-6">
          <Button 
            onClick={generateToken} 
            disabled={loading}
            variant="default"
          >
            {loading ? 'Создание...' : 'Создать новый токен'}
          </Button>
        </div>

        {loadingTokens ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Загрузка токенов...</span>
          </div>
        ) : tokens.length > 0 ? (
          <div className="space-y-3">
            <h3 className="font-medium">Ваши токены:</h3>
            {tokens.map((token, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex-1">
                  <code className="text-sm bg-background px-2 py-1 rounded">
                    {token.token}
                  </code>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(token.token)}
                  >
                    Копировать
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => deleteToken(token.token)}
                  >
                    Удалить
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : !error && (
          <div className="text-center py-4 text-muted-foreground">
            У вас пока нет API токенов. Создайте первый токен для начала работы с API.
          </div>
        )}
      </Card>

      {/* Документация API */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Использование API</h2>
        
        <div className="space-y-6">
          {/* Базовая информация */}
          <div>
            <h3 className="text-lg font-medium mb-2">Базовый URL</h3>
            <code className="block bg-muted p-3 rounded text-sm">
              {window.location.origin}/api
            </code>
          </div>

          {/* Поиск */}
          <div>
            <h3 className="text-lg font-medium mb-2">Поиск людей</h3>
            <p className="text-muted-foreground mb-3">
              Основной метод для поиска информации о людях по различным параметрам.
            </p>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium">Endpoint:</h4>
                <code className="block bg-muted p-3 rounded text-sm">
                  POST /api/search
                </code>
              </div>

              <div>
                <h4 className="font-medium">Параметры запроса:</h4>
                <div className="bg-muted p-4 rounded text-sm">
                  <pre>{JSON.stringify({
                    "token": "ваш_api_токен",
                    "request": "Иванов Иван Иванович 01.01.1990",
                    "limit": 50
                  }, null, 2)}</pre>
                </div>
              </div>

              <div>
                <h4 className="font-medium">Типы поиска:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li><strong>По телефону:</strong> +79161234567 или 89161234567</li>
                  <li><strong>По ИНН:</strong> 1234567890 (10 цифр) или 123456789012 (12 цифр)</li>
                  <li><strong>По СНИЛС:</strong> 12345678901 (11 цифр)</li>
                  <li><strong>По email:</strong> example@domain.com</li>
                  <li><strong>По ФИО и дате:</strong> Иванов Иван Иванович 01.01.1990</li>
                  <li><strong>По ФИО и частичной дате:</strong> Иванов Иван 01.01</li>
                  <li><strong>По ФИО:</strong> Иванов Иван Иванович</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium">Пример ответа:</h4>
                <div className="bg-muted p-4 rounded text-sm">
                  <pre>{JSON.stringify({
                    "query": "Иванов Иван Иванович",
                    "search_id": "uuid-string",
                    "status": "ok",
                    "results_count": 5,
                    "total_count": 15,
                    "results_by_source": {
                      "persons": [
                        {
                          "first_name": "Иван",
                          "last_name": "Иванов",
                          "patronymic": "Иванович",
                          "birth_date": "1990-01-01",
                          "phones": ["79161234567"],
                          "source": "persons"
                        }
                      ]
                    },
                    "query_time_ms": 150
                  }, null, 2)}</pre>
                </div>
              </div>
            </div>
          </div>

          {/* Проверка баланса */}
          <div>
            <h3 className="text-lg font-medium mb-2">Проверка баланса</h3>
            <p className="text-muted-foreground mb-3">
              Проверка количества доступных запросов.
            </p>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium">Endpoint:</h4>
                <code className="block bg-muted p-3 rounded text-sm">
                  POST /api/balance
                </code>
              </div>

              <div>
                <h4 className="font-medium">Параметры запроса:</h4>
                <div className="bg-muted p-4 rounded text-sm">
                  <pre>{JSON.stringify({
                    "token": "ваш_api_токен"
                  }, null, 2)}</pre>
                </div>
              </div>

              <div>
                <h4 className="font-medium">Пример ответа:</h4>
                <div className="bg-muted p-4 rounded text-sm">
                  <pre>{JSON.stringify({
                    "status": "ok",
                    "user_id": 123,
                    "balance": 50,
                    "balance_status": "limited",
                    "role": "Пользователь"
                  }, null, 2)}</pre>
                </div>
              </div>
            </div>
          </div>

          {/* Управление токенами */}
          <div>
            <h3 className="text-lg font-medium mb-2">Управление токенами</h3>
            <p className="text-muted-foreground mb-3">
              Методы для управления API токенами: получение списка, создание и удаление.
            </p>
            
            <div className="space-y-6">
              {/* Получение токенов */}
              <div>
                <h4 className="font-medium mb-2">Получение списка токенов</h4>
                <div className="space-y-3">
                  <div>
                    <h5 className="text-sm font-medium">Endpoint:</h5>
                    <code className="block bg-muted p-2 rounded text-sm">
                      GET /api/tokens
                    </code>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Аутентификация:</h5>
                    <p className="text-sm text-muted-foreground">Требуется авторизация через cookies</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Пример ответа:</h5>
                    <div className="bg-muted p-3 rounded text-sm">
                      <pre>{JSON.stringify({
                        "api_tokens": [
                          {
                            "token": "abc123def456ghi789",
                            "created_at": null,
                            "last_used": null
                          }
                        ]
                      }, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Создание токена */}
              <div>
                <h4 className="font-medium mb-2">Создание нового токена</h4>
                <div className="space-y-3">
                  <div>
                    <h5 className="text-sm font-medium">Endpoint:</h5>
                    <code className="block bg-muted p-2 rounded text-sm">
                      POST /api/generate_token
                    </code>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Аутентификация:</h5>
                    <p className="text-sm text-muted-foreground">Требуется авторизация через cookies</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Ограничения:</h5>
                    <p className="text-sm text-muted-foreground">Обычные пользователи могут иметь максимум 1 токен</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Пример ответа (успех):</h5>
                    <div className="bg-muted p-3 rounded text-sm">
                      <pre>{JSON.stringify({
                        "ok": true,
                        "token": "новый_сгенерированный_токен"
                      }, null, 2)}</pre>
                    </div>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Пример ответа (ошибка):</h5>
                    <div className="bg-muted p-3 rounded text-sm">
                      <pre>{JSON.stringify({
                        "ok": false,
                        "error": "У вас уже есть токен (больше одного нельзя)."
                      }, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Удаление токена */}
              <div>
                <h4 className="font-medium mb-2">Удаление токена</h4>
                <div className="space-y-3">
                  <div>
                    <h5 className="text-sm font-medium">Endpoint:</h5>
                    <code className="block bg-muted p-2 rounded text-sm">
                      DELETE /api/delete_token/&#123;token&#125;
                    </code>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Аутентификация:</h5>
                    <p className="text-sm text-muted-foreground">Требуется авторизация через cookies</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Параметры URL:</h5>
                    <p className="text-sm text-muted-foreground">token - токен для удаления</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Пример ответа (успех):</h5>
                    <div className="bg-muted p-3 rounded text-sm">
                      <pre>{JSON.stringify({
                        "ok": true,
                        "message": "Token deleted successfully"
                      }, null, 2)}</pre>
                    </div>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Пример ответа (ошибка):</h5>
                    <div className="bg-muted p-3 rounded text-sm">
                      <pre>{JSON.stringify({
                        "ok": false,
                        "error": "Указанный токен не найден среди ваших."
                      }, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Получение токенов для дашборда */}
              <div>
                <h4 className="font-medium mb-2">Получение токенов (дашборд)</h4>
                <div className="space-y-3">
                  <div>
                    <h5 className="text-sm font-medium">Endpoint:</h5>
                    <code className="block bg-muted p-2 rounded text-sm">
                      GET /dashboard/api/tokens
                    </code>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Аутентификация:</h5>
                    <p className="text-sm text-muted-foreground">Требуется авторизация через cookies</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">Пример ответа:</h5>
                    <div className="bg-muted p-3 rounded text-sm">
                      <pre>{JSON.stringify({
                        "ok": true,
                        "data": {
                          "user": {
                            "id": 123,
                            "username": "user@example.com",
                            "role": "Пользователь"
                          },
                          "api_tokens": ["token1", "token2"]
                        }
                      }, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Примеры кода */}
          <div>
            <h3 className="text-lg font-medium mb-2">Примеры кода</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium">Python (requests):</h4>
                <div className="bg-muted p-4 rounded text-sm">
                  <pre>{`import requests

# Поиск
response = requests.post('${window.location.origin}/api/search', json={
    'token': 'ваш_токен',
    'request': 'Иванов Иван Иванович',
    'limit': 50
})

data = response.json()
print(f"Ответ: {data}")

# Проверка баланса
balance_response = requests.post('${window.location.origin}/api/balance', json={
    'token': 'ваш_токен'
})

balance_data = balance_response.json()
print(f"Баланс: {balance_data['balance']} запросов")

# Получение списка токенов (требует авторизацию)
session = requests.Session()
# Сначала авторизуйтесь через веб-интерфейс или API
tokens_response = session.get('${window.location.origin}/api/tokens')
tokens_data = tokens_response.json()
print(f"Токены: {tokens_data['api_tokens']}")

# Создание нового токена (требует авторизацию)
create_response = session.post('${window.location.origin}/api/generate_token')
create_data = create_response.json()
if create_data['ok']:
    print(f"Новый токен: {create_data['token']}")
else:
    print(f"Ошибка: {create_data['error']}")

# Удаление токена (требует авторизацию)
delete_response = session.delete('${window.location.origin}/api/delete_token/токен_для_удаления')
delete_data = delete_response.json()
print(f"Результат удаления: {delete_data}")`}</pre>
                </div>
              </div>

              <div>
                <h4 className="font-medium">JavaScript (fetch):</h4>
                <div className="bg-muted p-4 rounded text-sm">
                  <pre>{`// Поиск
const searchResponse = await fetch('${window.location.origin}/api/search', {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        token: 'ваш_токен',
        request: 'Иванов Иван Иванович',
        limit: 50
    })
});

const searchData = await searchResponse.json();
console.log(\`Ответ: \${searchData}\`);

// Проверка баланса
const balanceResponse = await fetch('${window.location.origin}/api/balance', {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json'
    },
    body: JSON.stringify({
        token: 'ваш_токен'
    })
});

const balanceData = await balanceResponse.json();
console.log(\`Баланс: \${balanceData.balance} запросов\`);

// Получение списка токенов (требует авторизацию через cookies)
const tokensResponse = await fetch('${window.location.origin}/api/tokens', {
    method: 'GET',
    credentials: 'include',
    headers: {
        'Content-Type': 'application/json'
    }
});

const tokensData = await tokensResponse.json();
console.log(\`Токены: \${tokensData.api_tokens}\`);

// Создание нового токена (требует авторизацию через cookies)
const createResponse = await fetch('${window.location.origin}/api/generate_token', {
    method: 'POST',
    credentials: 'include',
    headers: {
        'Content-Type': 'application/json'
    }
});

const createData = await createResponse.json();
if (createData.ok) {
    console.log(\`Новый токен: \${createData.token}\`);
} else {
    console.log(\`Ошибка: \${createData.error}\`);
}

// Удаление токена (требует авторизацию через cookies)
const deleteResponse = await fetch('${window.location.origin}/api/delete_token/токен_для_удаления', {
    method: 'DELETE',
    credentials: 'include',
    headers: {
        'Content-Type': 'application/json'
    }
});

const deleteData = await deleteResponse.json();
console.log(\`Результат удаления: \${deleteData}\`);`}</pre>
                </div>
              </div>

              <div>
                <h4 className="font-medium">cURL:</h4>
                <div className="bg-muted p-4 rounded text-sm">
                  <pre>{`# Поиск
curl -X POST ${window.location.origin}/api/search \\
  -H "Content-Type: application/json" \\
  -d '{
    "token": "ваш_токен",
    "request": "Иванов Иван Иванович",
    "limit": 50
  }'

# Проверка баланса
curl -X POST ${window.location.origin}/api/balance \\
  -H "Content-Type: application/json" \\
  -d '{
    "token": "ваш_токен"
  }'

# Получение списка токенов (требует авторизацию через cookies)
curl -X GET ${window.location.origin}/api/tokens \\
  -H "Content-Type: application/json" \\
  --cookie-jar cookies.txt \\
  --cookie cookies.txt

# Создание нового токена (требует авторизацию через cookies)
curl -X POST ${window.location.origin}/api/generate_token \\
  -H "Content-Type: application/json" \\
  --cookie-jar cookies.txt \\
  --cookie cookies.txt

# Удаление токена (требует авторизацию через cookies)
curl -X DELETE ${window.location.origin}/api/delete_token/токен_для_удаления \\
  -H "Content-Type: application/json" \\
  --cookie-jar cookies.txt \\
  --cookie cookies.txt

# Получение токенов для дашборда (требует авторизацию через cookies)
curl -X GET ${window.location.origin}/dashboard/api/tokens \\
  -H "Content-Type: application/json" \\
  --cookie-jar cookies.txt \\
  --cookie cookies.txt`}</pre>
                </div>
              </div>
            </div>
          </div>

          {/* Ограничения и рекомендации */}
          <div>
            <h3 className="text-lg font-medium mb-2">Ограничения и рекомендации</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>Минимальный интервал между запросами: 0.5 секунды</li>
              <li>Максимальный лимит результатов: 10,000 на запрос</li>
              <li>Минимальный лимит результатов: 10 на запрос</li>
              <li>Таймаут запроса: 5 секунд</li>
              <li>Каждый поисковый запрос списывает 1 запрос с баланса</li>
            </ul>
          </div>

          {/* Коды ошибок */}
          <div>
            <h3 className="text-lg font-medium mb-2">Коды ошибок</h3>
            <div className="space-y-2 text-sm">
              <div><code>400</code> - Неверные параметры запроса</div>
              <div><code>401</code> - Неверный или отсутствующий токен</div>
              <div><code>402</code> - Недостаточно запросов на балансе</div>
              <div><code>429</code> - Превышен лимит частоты запросов</div>
              <div><code>500</code> - Внутренняя ошибка сервера</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
